import React, { useState, useEffect, useCallback } from 'react';
import { contentAPI } from '../api/content';
import { creatorsAPI } from '../api/creators';
import { gamesAPI } from '../api/games';
import { campaignsAPI } from '../api/campaigns';
import SegmentManager from './SegmentManager';
import { getFollowerLabel } from '../utils/platformHelpers';
import PlatformIcon from './PlatformIcon';

// Proper platform names — "YouTube"/"TikTok" casing is required for brand compliance.
// Never rely on CSS `capitalize` (that produces "Youtube"/"Tiktok").
const PLATFORM_LABELS = { youtube: 'YouTube', tiktok: 'TikTok', twitch: 'Twitch', kick: 'Kick', instagram: 'Instagram', twitter: 'Twitter', x: 'X' };
const platformLabel = (p) => PLATFORM_LABELS[(p || '').toLowerCase()] || (p ? p.charAt(0).toUpperCase() + p.slice(1) : '');

// Strip the junk that rides along when a URL is copied out of a spreadsheet or
// chat: surrounding whitespace/tabs, straight or curly quotes, and trailing
// dots/commas. A real content URL never starts or ends with any of these.
// (Sept 2026: ~15 campaign links were saved with a trailing `"` → `%22`, which
// made TikTok/YouTube/X show "page not available" on click-through.)
const cleanPastedUrl = (raw) => (raw || '')
  .replace(/^[\s"'“”‘’]+/, '')
  .replace(/[\s"'“”‘’.,]+$/, '');

// Lighter version for the single-URL box while typing: quotes and tabs never
// belong anywhere in a URL, but a trailing dot might just be mid-typing
// ("https://www.tiktok." before the "com"), so dots are only trimmed on submit.
const stripQuotesAndTabs = (raw) => (raw || '').replace(/[\t"“”]/g, '').trim();

const AddContentModal = ({ isOpen, onClose, onSuccess, campaignId = null, campaignInfluencerId = null }) => {
  const [mode, setMode] = useState('bulk'); // 'bulk' or 'single'
  const [creators, setCreators] = useState([]);
  const [campaignCreators, setCampaignCreators] = useState([]);
  const [games, setGames] = useState([]);
  
  // Bulk mode state
  const [bulkUrls, setBulkUrls] = useState('');
  const [urlItems, setUrlItems] = useState([]);
  const [loadingPreviews, setLoadingPreviews] = useState(false);
  const [addingContent, setAddingContent] = useState(false);
  const [results, setResults] = useState(null);
  
  // Single mode state
  const [formData, setFormData] = useState({
    contentUrl: '',
    platform: 'youtube',
    contentType: 'video',
    creatorId: '',
    gameId: '',
    postDate: '',
    views: '',
    likes: '',
    comments: '',
    shares: '',
    peakCcv: '',
    avgCcv: '',
    streamDuration: '',
    vodViews: '',
    videoId: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [error, setError] = useState('');
  const [scrapedCreator, setScrapedCreator] = useState(null);
  const [segments, setSegments] = useState([]);
  const [campaignGame, setCampaignGame] = useState(null);

  const fetchCampaignDetails = useCallback(async () => {
    try {
      const response = await campaignsAPI.getLite(campaignId);
      const campaign = response.data.campaign || response.data;
      if (campaign && campaign.game) {
        setCampaignGame(campaign.game);
        setFormData(prev => ({
          ...prev,
          gameId: campaign.game.id
        }));
      }
      // Extract creators from campaign (API returns 'influencers' not 'campaignInfluencer')
      const influencers = campaign.influencers || campaign.campaignInfluencer || [];
      if (influencers.length > 0) {
        const campCreators = influencers
          .map(inf => inf.creator || inf.Creator)
          .filter(Boolean);
        setCampaignCreators(campCreators);
        console.log('[FetchCampaign] Campaign creators:', campCreators.length, campCreators);
      }
    } catch (err) {
      console.error('Failed to fetch campaign details:', err);
    }
  }, [campaignId]);

  useEffect(() => {
    if (isOpen) {
      fetchCreators();
      fetchGames();
      if (campaignId) {
        fetchCampaignDetails();
      }
    }
  }, [isOpen, campaignId, fetchCampaignDetails]);

  const fetchCreators = async () => {
    try {
      console.log('[FetchCreators] Starting fetch...');
      const response = await creatorsAPI.getAll({ limit: 2000, offset: 0 });
      console.log('[FetchCreators] Full response:', response);
      console.log('[FetchCreators] response.data:', response.data);
      const creatorsData = response.data.creators || response.data;
      console.log('[FetchCreators] Setting creators:', creatorsData?.length || 0, 'creators', creatorsData);
      setCreators(creatorsData || []);
    } catch (err) {
      console.error('[FetchCreators] Error:', err);
      setCreators([]);
    }
  };

  const fetchGames = async () => {
    try {
      const response = await gamesAPI.getAll({ limit: 200 });
      setGames(response.data.games);
      return response.data.games;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // Helper: find matching creator from all available creators
  const findMatchingCreator = (item, allCreatorsList) => {
    if (!item) return null;
    const handleLower = (item.creatorHandle || '').toLowerCase();
    const nameLower = (item.creatorName || '').toLowerCase();
    const ytChannelId = item.youtubeChannelId || '';
    const platform = item.platform;

    const uniqueCreators = Array.from(new Map(allCreatorsList.map(c => [c.id, c])).values());

    return uniqueCreators.find(c => {
      if (platform === 'youtube' && ytChannelId && c.channelId === ytChannelId) return true;
      if (platform === 'youtube' && ytChannelId && c.youtubeUrl &&
          c.youtubeUrl.includes(ytChannelId)) return true;
      if (handleLower && c.handle?.toLowerCase() === handleLower) return true;
      if (nameLower && c.name?.toLowerCase() === nameLower) return true;
      // NOTE: every check below must be guarded by `handleLower` being non-empty.
      // `''.includes()`/`url.includes('')` is always true, so an empty handle (e.g. a
      // failed Instagram scrape) would otherwise match the FIRST creator with that URL
      // and silently mis-attribute the post. Refuse to match rather than guess.
      if (platform === 'tiktok' && handleLower && c.tiktokUsername &&
          c.tiktokUsername.toLowerCase() === handleLower) return true;
      if (platform === 'tiktok' && handleLower && c.tiktokUrl &&
          c.tiktokUrl.toLowerCase().includes(handleLower)) return true;
      if (platform === 'instagram' && handleLower && c.instagramUrl &&
          c.instagramUrl.toLowerCase().includes(handleLower)) return true;
      if (platform === 'kick' && handleLower) {
        if (c.kickSlug && c.kickSlug.toLowerCase() === handleLower) return true;
        if (c.kickUrl && (c.kickUrl.toLowerCase().match(/kick\.com\/([a-z0-9_-]+)/) || [])[1] === handleLower) return true;
      }
      if (platform === 'twitter' && handleLower) {
        if (c.twitterUsername &&
            c.twitterUsername.toLowerCase().replace(/^@/, '') === handleLower) return true;
        // twitterUrl is e.g. https://twitter.com/ChratosGameplay or https://x.com/...
        if (c.twitterUrl) {
          const urlHandle = (c.twitterUrl.toLowerCase().match(/(?:twitter|x)\.com\/@?([a-z0-9_]+)/) || [])[1];
          if (urlHandle === handleLower) return true;
        }
      }
      return false;
    });
  };

  // Re-run auto-match when campaign creators or global creators load
  useEffect(() => {
    if (urlItems.length === 0) return;
    const allCreators = [...campaignCreators, ...creators];
    if (allCreators.length === 0) return;

    const hasUnmatched = urlItems.some(item =>
      !item.creatorId && (item.creatorHandle || item.creatorName || item.youtubeChannelId)
    );
    if (!hasUnmatched) return;

    setUrlItems(items => items.map(item => {
      if (item.creatorId) return item; // already matched
      // Instagram: never auto-match a creator — the team must pick explicitly.
      // Fuzzy name/handle matching was landing posts on the wrong creator.
      if (item.platform === 'instagram') return item;
      const match = findMatchingCreator(item, allCreators);
      if (match) {
        console.log(`[AutoMatch] Matched "${item.creatorName || item.creatorHandle}" to "${match.name}" (${match.id})`);
        return { ...item, creatorId: match.id };
      }
      return item;
    }));
  }, [campaignCreators, creators]);

  // ===== BULK MODE FUNCTIONS =====

  const parseUrls = (text) => {
    // Split by newlines, commas, tabs and quote marks. URLs copied out of a
    // spreadsheet/CSV arrive wrapped in quotes (sometimes two glued together
    // by a stray quote), so quotes act as separators rather than URL characters.
    return text
      .split(/[\n\t,"“”]+/)
      .map(cleanPastedUrl)
      .filter(url => url.length > 0)
      .filter(url => url.includes('twitch.tv') || url.includes('kick.com') || url.includes('youtube.com') || url.includes('youtu.be') || url.includes('tiktok.com') || url.includes('instagram.com') || url.includes('twitter.com') || url.includes('x.com'));
  };

  const extractVideoId = (url) => {
    // YouTube: extract video ID
    if (url.includes('youtube.com')) {
      // Handle Shorts URLs: /shorts/VIDEO_ID
      const shortsMatch = url.match(/\/shorts\/([a-zA-Z0-9_-]+)/);
      if (shortsMatch) return shortsMatch[1];
      // Handle regular video URLs: v=VIDEO_ID
      const match = url.match(/v=([a-zA-Z0-9_-]+)/);
      return match ? match[1] : null;
    }
    if (url.includes('youtu.be')) {
      const match = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
      return match ? match[1] : null;
    }
    // Twitch: extract video ID
    if (url.includes('twitch.tv')) {
      const match = url.match(/videos\/(\d+)/);
      if (match) return match[1];
      const clipMatch = url.match(/clips\.twitch\.tv\/([a-zA-Z0-9_-]+)/);
      if (clipMatch) return clipMatch[1];
    }
    // Kick: VOD uuid from kick.com/<slug>/videos/<uuid>; a bare channel URL has no id,
    // so fall back to the slug (the backend treats it as a live/stream reference).
    if (url.includes('kick.com')) {
      const vodMatch = url.match(/\/videos\/([0-9a-f-]{20,})/i);
      if (vodMatch) return vodMatch[1];
      const slugMatch = url.match(/kick\.com\/([A-Za-z0-9_-]+)/);
      if (slugMatch) return slugMatch[1].toLowerCase();
    }
    // TikTok: extract video ID
    if (url.includes('tiktok.com')) {
      const match = url.match(/\/video\/(\d+)/);
      if (match) return match[1];
      // Short URL format
      const shortMatch = url.match(/vm\.tiktok\.com\/([a-zA-Z0-9]+)/);
      if (shortMatch) return shortMatch[1];
    }
    // Instagram: extract post ID
    if (url.includes('instagram.com')) {
      // Handle /p/POST_ID/ (posts) and /reel/REEL_ID/ (reels)
      const postMatch = url.match(/\/(p|reel|reels)\/([a-zA-Z0-9_-]+)/);
      if (postMatch) return postMatch[2];
    }
    // Twitter/X: extract post ID
    if (url.includes('twitter.com') || url.includes('x.com')) {
      // Handle twitter.com/user/status/ID or x.com/user/status/ID
      const statusMatch = url.match(/\/status\/(\d+)/);
      if (statusMatch) return statusMatch[1];
    }
    return null;
  };

  const detectContentType = (url) => {
    if (url.includes('/videos/') || url.includes('/v/')) return 'vod';
    if (url.includes('clips.twitch.tv') || url.includes('/clip/')) return 'clip';
    if (url.includes('/shorts/')) return 'short';
    if (url.includes('/reel')) return 'reel';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'video';
    if (url.includes('instagram.com')) return 'post';
    if (url.includes('twitter.com') || url.includes('x.com')) return 'post';
    return 'video';
  };

  const detectPlatform = (url) => {
    if (url.includes('twitch.tv')) return 'twitch';
    if (url.includes('kick.com')) return 'kick';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('tiktok.com')) return 'tiktok';
    if (url.includes('instagram.com')) return 'instagram';
    if (url.includes('twitter.com') || url.includes('x.com')) return 'twitter';
    return 'youtube';
  };

  const fetchPreview = async (url) => {
    try {
      const platform = detectPlatform(url);
      const contentType = detectContentType(url);
      const videoId = extractVideoId(url);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/content/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ url, platform, contentType, videoId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Preview fetch failed');
      }
      
      const data = await response.json();

      // Belt-and-braces for Twitter: the author handle is always in the URL
      // (x.com/<handle>/status/<id>) — derive it when the preview couldn't
      // scrape metadata, so creator auto-match still works.
      if (platform === 'twitter' && !data.creatorHandle) {
        const urlHandle = (url.match(/(?:twitter|x)\.com\/([A-Za-z0-9_]+)\/status/i) || [])[1];
        if (urlHandle) data.creatorHandle = urlHandle;
      }

      // Try to auto-match creator (useEffect will retry if creators haven't loaded yet).
      // Instagram is excluded — the team must pick the creator explicitly, because
      // fuzzy name/handle matching was landing posts on the wrong creator.
      const allCreators = [...campaignCreators, ...creators];
      const matchedCreator = platform === 'instagram' ? null : findMatchingCreator({ ...data, platform }, allCreators);

      // Classify the preview status:
      // - 'quota-limited': YouTube API actually returned a quota error
      // - 'fallback': API succeeded but had no data (private/deleted/unlisted) or fell back for other reasons
      // - 'ready': data came from the official API
      const isYouTube = platform === 'youtube';
      const usedFallback = isYouTube && data.dataSource && data.dataSource !== 'api';
      let status = 'ready';
      let errorMsg = null;
      if (data.quotaLimited) {
        status = 'quota-limited';
        errorMsg = 'YouTube quota limited - basic info loaded';
      } else if (data.apiReturnedNoItems) {
        status = 'fallback';
        errorMsg = 'Video unavailable via YouTube API (private/deleted/unlisted?) - using fallback data';
      } else if (usedFallback) {
        status = 'fallback';
        errorMsg = `YouTube API skipped - using ${data.dataSource} fallback`;
      }

      const item = {
        url,
        title: data.title || 'Untitled',
        thumbnail: data.thumbnail || '',
        duration: data.duration || 0,
        views: data.views || 0,
        likes: data.likes || 0,
        comments: data.comments || 0,
        shares: data.shares || 0,
        creatorHandle: data.creatorHandle || '',
        creatorName: data.creatorName || '',
        creatorId: matchedCreator?.id || '',
        platform: platform,
        contentType: contentType,
        videoId: videoId,
        youtubeChannelId: data.youtubeChannelId || '',
        postDate: data.postDate || null,  // CAPTURE BROADCAST DATE FROM METADATA
        status,
        error: errorMsg,
        dataSource: data.dataSource,
        selected: true  // EXPLICITLY set to true
      };
      
      console.log('Fetched preview:', item.title, 'selected:', item.selected);
      return item;
    } catch (err) {
      const item = {
        url,
        title: 'Failed to load',
        thumbnail: '',
        duration: 0,
        views: 0,
        likes: 0,
        creatorHandle: '',
        creatorName: '',
        creatorId: '',
        platform: detectPlatform(url),
        contentType: detectContentType(url),
        videoId: extractVideoId(url),
        status: 'error',
        error: err.message,
        selected: true  // EXPLICITLY set to true even on error
      };
      console.log('Failed to fetch preview:', url, 'selected:', item.selected);
      return item;
    }
  };

  const handleBulkInput = async (e) => {
    const text = e.target.value;
    setBulkUrls(text);

    const urls = parseUrls(text);
    console.log('[AddContent] Parsed URLs:', urls);
    if (urls.length === 0) {
      setUrlItems([]);
      return;
    }

    setLoadingPreviews(true);
    try {
      const previews = await Promise.all(urls.map(url => fetchPreview(url)));
      setUrlItems(previews);
    } catch (err) {
      setError('Failed to load some previews');
    } finally {
      setLoadingPreviews(false);
    }
  };

  const toggleUrlItem = (index) => {
    setUrlItems(items => {
      const updated = items.map((item, i) =>
        i === index ? { ...item, selected: !item.selected } : item
      );
      console.log(`Toggled item ${index}. New state:`, updated[index].selected, 'Title:', updated[index].title);
      console.log('All selected items:', updated.filter(i => i.selected).length, 'out of', updated.length);
      return updated;
    });
  };

  const updateUrlItemCreator = (index, creatorId) => {
    setUrlItems(items =>
      items.map((item, i) =>
        i === index ? { ...item, creatorId } : item
      )
    );
  };

  const updateUrlItemManualCreator = (index, manualCreatorName) => {
    setUrlItems(items =>
      items.map((item, i) => {
        if (i !== index) return item;

        // Just store the typed text, don't auto-select
        return { ...item, manualCreatorName, creatorId: '' };
      })
    );
  };

  const selectCreatorFromSuggestion = (index, creatorId) => {
    setUrlItems(items =>
      items.map((item, i) =>
        i === index ? { ...item, creatorId, manualCreatorName: '' } : item
      )
    );
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const resetForm = () => {
    setMode('bulk');
    setBulkUrls('');
    setUrlItems([]);
    setResults(null);
    setError('');
    setFormData({
      contentUrl: '', platform: 'youtube', contentType: 'video',
      creatorId: '', gameId: '', postDate: '', views: '', likes: '',
      comments: '', shares: '', peakCcv: '', avgCcv: '', streamDuration: '', vodViews: '', videoId: ''
    });
    setScrapedCreator(null);
    setSegments([]);
  };

  const handleBulkAdd = async () => {
    const selected = urlItems.filter(item => item.selected);
    if (selected.length === 0) {
      setError('Please select at least one item to add');
      return;
    }

    setAddingContent(true);
    setError('');

    const successItems = [];
    const duplicateItems = [];
    const failedItems = [];
    const rejectedItems = [];

    try {
      for (const item of selected) {
        try {
          // Allow either creatorId OR creatorHandle/creatorName/manualCreatorName for auto-creation
          if (!item.creatorId && !item.creatorHandle && !item.creatorName && !item.manualCreatorName) {
            failedItems.push({ title: item.title, reason: 'No creator info available' });
            continue;
          }

          const dataToSend = {
            contentUrl: item.url,
            platform: item.platform,
            contentType: item.contentType,
            creatorId: item.creatorId || null,
            creatorHandle: item.creatorHandle || null,
            creatorName: item.creatorName || item.manualCreatorName || null,
            gameId: campaignId ? campaignGame?.id : '',
            campaignId: campaignId,
            campaignInfluencerId: campaignInfluencerId,
            postDate: item.postDate || null,
            title: item.title || null,
            thumbnail: item.thumbnail || null,
            views: item.views || 0,
            likes: item.likes || 0,
            comments: item.comments || 0,
            shares: item.shares || 0,
            duration: item.duration || 0,
            videoId: item.videoId,
            youtubeChannelId: item.youtubeChannelId || null
          };

          console.log('Sending content data:', {
            title: item.title,
            creatorHandle: dataToSend.creatorHandle,
            creatorName: dataToSend.creatorName,
            creatorId: dataToSend.creatorId,
            manualCreatorName: item.manualCreatorName
          });

          const res = await contentAPI.create(dataToSend);
          if (res?.data?.rejected) {
            rejectedItems.push({ title: item.title, reason: res.data.reason });
          } else {
            successItems.push({ title: item.title });
          }
        } catch (err) {
          const errorMsg = err.response?.data?.error || err.message;
          if (errorMsg.toLowerCase().includes('already exists') || errorMsg.toLowerCase().includes('duplicate')) {
            duplicateItems.push({ title: item.title });
          } else {
            failedItems.push({ title: item.title, reason: errorMsg });
          }
        }
      }

      setResults({
        success: successItems,
        duplicates: duplicateItems,
        failed: failedItems,
        rejected: rejectedItems,
        total: selected.length
      });

      if (successItems.length > 0) {
        setTimeout(() => {
          onSuccess();
          resetForm();
        }, 2500);
      }
    } finally {
      setAddingContent(false);
    }
  };

  const handleUrlChange = async (e) => {
    const url = stripQuotesAndTabs(e.target.value);
    setFormData({ ...formData, contentUrl: url });

    // Auto-detect platform and content type for TikTok
    if (url.includes('tiktok.com')) {
      setFormData(prev => ({ ...prev, contentUrl: url, platform: 'tiktok', contentType: 'video' }));
    }

    // Auto-detect platform and content type for Instagram
    if (url.includes('instagram.com')) {
      const contentType = url.includes('/reel') ? 'reel' : 'post';
      setFormData(prev => ({ ...prev, contentUrl: url, platform: 'instagram', contentType }));
    }

    // Auto-detect platform and content type for Twitter/X
    if (url.includes('twitter.com') || url.includes('x.com')) {
      setFormData(prev => ({ ...prev, contentUrl: url, platform: 'twitter', contentType: 'post' }));
    }

    // Kick: no VOD API, so no auto-scrape — set platform/type and let the
    // stream fields (CCV, duration, VOD views) be typed in from the Kick videos tab.
    if (url.includes('kick.com')) {
      setFormData(prev => ({ ...prev, contentUrl: url, platform: 'kick', contentType: 'vod' }));
    }

    if (url.length > 20 && (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('twitch.tv') || url.includes('tiktok.com') || url.includes('instagram.com') || url.includes('twitter.com') || url.includes('x.com'))) {
      await scrapeUrl(url);
    }
  };

  const scrapeUrl = async (url) => {
    setScraping(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/scrape/url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ url })
      });

      const result = await response.json();

      if (result.success) {
        const data = result.data;

        // Auto-fill form with scraped data
        setFormData(prev => ({
          ...prev,
          platform: data.platform || prev.platform,
          contentType: data.contentType || prev.contentType,
          views: data.views?.toString() || '',
          likes: data.likes?.toString() || '',
          comments: data.comments?.toString() || '',
          shares: data.shares?.toString() || '',
          peakCcv: data.peakCcv?.toString() || '',
          avgCcv: data.avgCcv?.toString() || '',
          streamDuration: data.streamDuration?.toString() || '',
          vodViews: data.vodViews?.toString() || '',
          postDate: data.postDate ? new Date(data.postDate).toISOString().slice(0, 16) : '',
          creatorId: data.creator?.id || '',
          gameId: data.game?.id || '',
          videoId: data.videoId || ''  // NEW: Store videoId for VODs
        }));

        setScrapedCreator(data.creator);
        
        // AUTO-POPULATE SEGMENTS - NEW!
        if (data.segments && data.segments.length > 0) {
          const formattedSegments = data.segments.map((seg) => ({
            gameId: seg.gameId || '',
            gameName: seg.gameName || 'Unknown',
            durationMinutes: seg.durationMinutes || 0,
            avgCcv: seg.avgCcv || null,
            peakCcv: seg.peakCcv || null,
            startTimeSeconds: seg.startTimeSeconds || 0,
            segmentOrder: seg.segmentOrder || 0,
            isManual: false,
            isAutoDetected: seg.isAutoDetected !== false
          }));
          setSegments(formattedSegments);
          
          console.log(`Auto-detected ${formattedSegments.length} game segments!`);
        }

        // Refresh dropdowns
        await fetchCreators();
        await fetchGames();

        // Show info about multiple games
        if (data.hasMultipleGames) {
          console.log('This VOD contains multiple games - segments auto-populated!');
        }

      } else {
        setError(result.error || 'Failed to fetch content info');
      }
    } catch (err) {
      console.error('Scrape error:', err);
      setError('Failed to fetch content info. You can still enter manually.');
    } finally {
      setScraping(false);
    }
  };

  const createCreatorFromScraped = async () => {
    if (!scrapedCreator) return null;

    try {
      // If the scraped creator already has an ID, use it (creator already exists in DB)
      if (scrapedCreator.id) {
        return scrapedCreator.id;
      }

      // Try to fetch existing creator by platform + handle
      try {
        const response = await creatorsAPI.getAll({ 
          platform: scrapedCreator.platform,
          search: scrapedCreator.handle 
        });
        
        if (response.data.creators && response.data.creators.length > 0) {
          // Find exact match by handle
          const existing = response.data.creators.find(c => c.handle === scrapedCreator.handle);
          if (existing) {
            console.log('Found existing creator:', existing.id);
            return existing.id;
          }
        }
      } catch (searchErr) {
        console.log('Could not search for existing creator, will try to create');
      }

      // Creator doesn't exist, create it
      const response = await creatorsAPI.create({
        name: scrapedCreator.name,
        handle: scrapedCreator.handle,
        platform: scrapedCreator.platform,
        channelId: scrapedCreator.channelId,
        profileUrl: scrapedCreator.profileUrl,
        followerCount: scrapedCreator.followerCount
      });
      
      console.log('Created new creator:', response.data.creator.id);
      
      // Refresh creators list
      await fetchCreators();
      
      return response.data.creator.id;
    } catch (err) {
      console.error('Failed to create/fetch creator:', err);
      setError(`Creator issue: ${err.response?.data?.error || err.message}`);
      return null;
    }
  };

  if (!isOpen) return null;

  // eslint-disable-next-line no-unused-vars
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let creatorId = formData.creatorId;

      // If no creator selected but we have scraped creator data, create them
      if (!creatorId && scrapedCreator) {
        creatorId = await createCreatorFromScraped();
        if (!creatorId) {
          throw new Error('Failed to create creator');
        }
      }

      if (!creatorId) {
        throw new Error('Please select or add a creator');
      }

      const dataToSend = {
        contentUrl: cleanPastedUrl(formData.contentUrl),
        platform: formData.platform,
        contentType: formData.contentType,
        creatorId: creatorId,
        gameId: formData.gameId || null,
        campaignId: campaignId,
        campaignInfluencerId: campaignInfluencerId,
        postDate: formData.postDate ? new Date(formData.postDate).toISOString() : null,
        views: parseInt(formData.views) || 0,
        likes: parseInt(formData.likes) || 0,
        comments: parseInt(formData.comments) || 0,
        shares: parseInt(formData.shares) || 0,
        peakCcv: parseInt(formData.peakCcv) || null,
        avgCcv: parseInt(formData.avgCcv) || null,
        streamDuration: parseInt(formData.streamDuration) || null,
        vodViews: parseInt(formData.vodViews) || null,
        videoId: formData.videoId || null,  // NEW: Include videoId for VOD linkage
        segments: segments
      };

      const res = await contentAPI.create(dataToSend);
      if (res?.data?.rejected) {
        setError(res.data.reason || 'Content rejected — creator not on this campaign.');
        return;
      }
      onSuccess();
      onClose();

      // Reset form
      setFormData({
        contentUrl: '', platform: 'youtube', contentType: 'video',
        creatorId: '', gameId: '', postDate: '', views: '', likes: '',
        comments: '', shares: '', peakCcv: '', avgCcv: '', streamDuration: '', vodViews: '', videoId: ''
      });
      setScrapedCreator(null);
      setSegments([]);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let creatorId = formData.creatorId;

      if (!creatorId) {
        throw new Error('Please select a creator');
      }

      const dataToSend = {
        contentUrl: cleanPastedUrl(formData.contentUrl),
        platform: formData.platform,
        contentType: formData.contentType,
        creatorId: creatorId,
        gameId: formData.gameId || null,
        campaignId: campaignId,
        campaignInfluencerId: campaignInfluencerId,
        postDate: formData.postDate ? new Date(formData.postDate).toISOString() : null,
        views: parseInt(formData.views) || 0,
        likes: parseInt(formData.likes) || 0,
        comments: parseInt(formData.comments) || 0,
        shares: parseInt(formData.shares) || 0,
        peakCcv: parseInt(formData.peakCcv) || null,
        avgCcv: parseInt(formData.avgCcv) || null,
        streamDuration: parseInt(formData.streamDuration) || null,
        vodViews: parseInt(formData.vodViews) || null,
        videoId: formData.videoId || null
      };

      const res = await contentAPI.create(dataToSend);
      if (res?.data?.rejected) {
        setError(res.data.reason || 'Content rejected — creator not on this campaign.');
        return;
      }
      onSuccess();
      resetForm();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add content');
    } finally {
      setLoading(false);
    }
  };

  const showTwitchFields = (formData.platform === 'twitch' || formData.platform === 'kick') &&
    (formData.contentType === 'stream' || formData.contentType === 'vod');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#241d33] border border-white/15 rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/15 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">📹 Add Content</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">×</button>
        </div>

        {/* Mode Tabs */}
        <div className="flex border-b border-white/15 bg-white/5 px-6">
          <button
            onClick={() => { setMode('bulk'); setError(''); }}
            className={`px-4 py-3 font-medium border-b-2 transition ${
              mode === 'bulk'
                ? 'border-sc-pink text-sc-pink'
                : 'border-transparent text-white hover:text-white'
            }`}
          >
            📋 Bulk Upload
          </button>
          <button
            onClick={() => { setMode('single'); setError(''); }}
            className={`px-4 py-3 font-medium border-b-2 transition ${
              mode === 'single'
                ? 'border-sc-pink text-sc-pink'
                : 'border-transparent text-white hover:text-white'
            }`}
          >
            ➕ Single Upload
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* BULK MODE */}
          {mode === 'bulk' && (
            <div>
              {!results ? (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-white mb-2">
                      Paste URLs (one per line or comma-separated)
                    </label>
                    <textarea
                      value={bulkUrls}
                      onChange={handleBulkInput}
                      placeholder="https://www.youtube.com/watch?v=... (or Twitch VODs, Kick VODs, TikTok videos, Instagram posts)"
                      rows={4}
                      className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50 font-mono text-sm"
                    />
                    <p className="text-xs text-white mt-1">
                      💡 YouTube, Twitch, TikTok, and Instagram content supported
                    </p>
                  </div>

                  {/* URL Items Preview */}
                  {urlItems.length > 0 && (
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-semibold text-white">
                          {urlItems.length} item{urlItems.length !== 1 ? 's' : ''} found
                        </h3>
                        {loadingPreviews && (
                          <span className="text-xs text-sc-pink flex items-center gap-1">
                            <div className="animate-spin h-3 w-3 border-2 border-sc-pink border-t-transparent rounded-full"></div>
                            Loading previews...
                          </span>
                        )}
                      </div>

                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {urlItems.map((item, idx) => (
                          <div
                            key={idx}
                            className={`border rounded-lg p-4 ${
                              item.status === 'error'
                                ? 'border-red-500/30 bg-red-500/10'
                                : (item.status === 'quota-limited' || item.status === 'fallback')
                                ? 'border-yellow-500/30 bg-yellow-500/10'
                                : 'border-white/15 bg-white/5'
                            }`}
                          >
                            <div className="flex gap-4">
                              {/* Checkbox - ensure it's properly controlled */}
                              <div className="flex-shrink-0">
                                <input
                                  type="checkbox"
                                  checked={item.selected === true}
                                  onChange={() => toggleUrlItem(idx)}
                                  className="mt-1 h-5 w-5 text-purple-600 rounded border-white/20 focus:ring-purple-500 cursor-pointer"
                                />
                              </div>

                              {/* Info */}
                              <div className="flex-grow">
                                <p className="font-medium text-white truncate">{item.title}</p>
                                <div className="flex items-center gap-2 mt-1 text-xs text-white flex-wrap">
                                  <span className="bg-white/10 px-2 py-0.5 rounded">
                                    {platformLabel(item.platform)}
                                  </span>
                                  {item.views > 0 && (
                                    <span>📊 {formatNumber(item.views)} views</span>
                                  )}
                                  {item.views === null && (
                                    <span className="text-yellow-300" title="Views unavailable - API quota exceeded">
                                      👁️ N/A
                                    </span>
                                  )}
                                  {item.dataSource && item.dataSource !== 'api' && (
                                    <span className="text-xs text-white/80" title={`Data from ${item.dataSource}`}>
                                      ({item.dataSource})
                                    </span>
                                  )}
                                  {item.error && (
                                    <span className={(item.status === 'quota-limited' || item.status === 'fallback') ? 'text-yellow-300' : 'text-red-300'}>
                                      {(item.status === 'quota-limited' || item.status === 'fallback') ? '⚠️' : '❌'} {item.error}
                                    </span>
                                  )}
                                </div>

                                {/* Creator Selection */}
                                <div className="mt-2">
                                  {(item.status === 'quota-limited' || item.status === 'fallback') && !item.creatorHandle && !item.creatorName && !item.manualCreatorName && (
                                    <p className="text-xs text-yellow-300 mb-1">
                                      {item.status === 'quota-limited'
                                        ? 'YouTube quota limited - please select a creator manually or type a name below'
                                        : 'Creator info unavailable - please select a creator manually or type a name below'}
                                    </p>
                                  )}
                                  {item.status === 'quota-limited' && (item.creatorHandle || item.creatorName) && (
                                    <p className="text-xs text-yellow-300 mb-1">⚠️ YouTube quota limited - but creator info was found</p>
                                  )}
                                  {item.creatorId && (() => {
                                    const onRoster = campaignCreators.some(c => c.id === item.creatorId);
                                    const matched = [...campaignCreators, ...creators].find(c => c.id === item.creatorId);
                                    if (!matched) return null;
                                    return onRoster ? (
                                      <p className="text-xs text-green-300 mb-1">✓ Will attach to {matched.name} (@{matched.handle})</p>
                                    ) : (
                                      <p className="text-xs text-white mb-1">⚠️ {matched.name} isn't on this campaign — this will be rejected. Add them via "Add Creators" first.</p>
                                    );
                                  })()}
                                  {!item.creatorId && (item.creatorHandle || item.creatorName || item.manualCreatorName) && (
                                    item.platform === 'instagram' ? (
                                      <p className="text-xs text-white mb-1">Instagram posts aren't auto-matched — pick the creator for this post from the roster below.</p>
                                    ) : (
                                      <p className="text-xs text-white mb-1">⚠️ "{item.manualCreatorName || item.creatorName || item.creatorHandle}" isn't on this campaign — this will be rejected. Add them via "Add Creators" first, or pick a roster creator below.</p>
                                    )
                                  )}
                                  {!item.creatorHandle && !item.creatorName && (
                                    <select
                                      value={item.creatorId || ''}
                                      onChange={(e) => updateUrlItemCreator(idx, e.target.value)}
                                      className={`w-full px-2 py-1 border rounded text-sm text-white bg-white/5 ${
                                        item.creatorId
                                          ? 'border-green-500/40 bg-green-500/10'
                                          : item.manualCreatorName
                                          ? 'border-white/20'
                                          : 'border-red-500/40 bg-red-500/10'
                                      }`}
                                    >
                                      <option value="">Select creator...</option>
                                      {Array.from(new Map(campaignCreators.map(c => [c.id, c])).values())
                                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                        .map(c => (
                                        <option key={c.id} value={c.id}>
                                          {c.name} (@{c.handle})
                                        </option>
                                      ))}
                                    </select>
                                  )}

                                  {/* Manual creator name input with autocomplete - always show when no auto-detected creator */}
                                  {!item.creatorHandle && !item.creatorName && (
                                    <div className="mt-2 relative">
                                      <div className="text-xs text-white mb-1">
                                        Or search a creator on this campaign:
                                      </div>
                                      <input
                                        type="text"
                                        value={item.manualCreatorName || ''}
                                        onChange={(e) => updateUrlItemManualCreator(idx, e.target.value)}
                                        placeholder="Enter creator name..."
                                        className={`w-full px-2 py-1 border rounded text-sm text-white bg-white/5 ${
                                          item.manualCreatorName
                                            ? 'border-sc-pink bg-sc-pink/10'
                                            : 'border-white/20'
                                        }`}
                                      />
                                      {/* Autocomplete suggestions */}
                                      {item.manualCreatorName && item.manualCreatorName.length > 0 && (() => {
                                        const searchTerm = item.manualCreatorName.toLowerCase();
                                        // Combine campaign creators and all creators, prioritizing campaign creators
                                        const allCreatorsList = [...campaignCreators, ...creators];
                                        // Remove duplicates by id
                                        const uniqueCreators = Array.from(
                                          new Map(allCreatorsList.map(c => [c.id, c])).values()
                                        );
                                        const filtered = uniqueCreators.filter(c =>
                                          c.name.toLowerCase().includes(searchTerm) ||
                                          c.handle?.toLowerCase().includes(searchTerm)
                                        ).slice(0, 10);

                                        return (
                                          <div className="absolute z-10 w-full mt-1 bg-[#241d33] border border-white/20 rounded shadow-lg max-h-60 overflow-y-auto">
                                            {filtered.map(c => (
                                              <div
                                                key={c.id}
                                                onClick={() => selectCreatorFromSuggestion(idx, c.id)}
                                                className="px-3 py-2 hover:bg-sc-pink/10 cursor-pointer border-b border-white/10 last:border-b-0"
                                              >
                                                <div className="font-medium text-sm">{c.name}</div>
                                                <div className="text-xs text-white">@{c.handle}</div>
                                              </div>
                                            ))}
                                            {filtered.length === 0 && (
                                              <div className="px-3 py-2 text-sm text-white">
                                                Not on this campaign — add "{item.manualCreatorName}" via "Add Creators" first, then re-add the content.
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  {(item.creatorHandle || item.creatorName) && (
                                    <div className="text-xs text-white mt-1">
                                      Or select from existing creators:
                                    </div>
                                  )}
                                  {(item.creatorHandle || item.creatorName) && (
                                    <select
                                      value={item.creatorId || ''}
                                      onChange={(e) => updateUrlItemCreator(idx, e.target.value)}
                                      className={`w-full px-2 py-1 border rounded text-sm text-white bg-white/5 mt-1 ${
                                        item.creatorId
                                          ? 'border-green-500/40 bg-green-500/10'
                                          : 'border-white/20'
                                      }`}
                                    >
                                      <option value="">— pick a creator in the campaign —</option>
                                      {Array.from(new Map(campaignCreators.map(c => [c.id, c])).values())
                                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                        .map(c => (
                                        <option key={c.id} value={c.id}>
                                          {c.name} (@{c.handle})
                                        </option>
                                      ))}
                                    </select>
                                  )}

                                  {/* Manual creator name input with autocomplete - also show for auto-detected creators */}
                                  {(item.creatorHandle || item.creatorName) && (
                                    <div className="mt-2 relative">
                                      <div className="text-xs text-white mb-1">
                                        Or search a different roster creator:
                                      </div>
                                      <input
                                        type="text"
                                        value={item.manualCreatorName || ''}
                                        onChange={(e) => updateUrlItemManualCreator(idx, e.target.value)}
                                        placeholder="Enter creator name..."
                                        className={`w-full px-2 py-1 border rounded text-sm text-white bg-white/5 ${
                                          item.manualCreatorName
                                            ? 'border-sc-pink bg-sc-pink/10'
                                            : 'border-white/20'
                                        }`}
                                      />
                                      {/* Autocomplete suggestions */}
                                      {item.manualCreatorName && item.manualCreatorName.length > 0 && (() => {
                                        const searchTerm = item.manualCreatorName.toLowerCase();
                                        // Combine campaign creators and all creators, prioritizing campaign creators
                                        const allCreatorsList = [...campaignCreators, ...creators];
                                        // Remove duplicates by id
                                        const uniqueCreators = Array.from(
                                          new Map(allCreatorsList.map(c => [c.id, c])).values()
                                        );
                                        const filtered = uniqueCreators.filter(c =>
                                          c.name.toLowerCase().includes(searchTerm) ||
                                          c.handle?.toLowerCase().includes(searchTerm)
                                        ).slice(0, 10);

                                        return (
                                          <div className="absolute z-10 w-full mt-1 bg-[#241d33] border border-white/20 rounded shadow-lg max-h-60 overflow-y-auto">
                                            {filtered.map(c => (
                                              <div
                                                key={c.id}
                                                onClick={() => selectCreatorFromSuggestion(idx, c.id)}
                                                className="px-3 py-2 hover:bg-sc-pink/10 cursor-pointer border-b border-white/10 last:border-b-0"
                                              >
                                                <div className="font-medium text-sm">{c.name}</div>
                                                <div className="text-xs text-white">@{c.handle}</div>
                                              </div>
                                            ))}
                                            {filtered.length === 0 && (
                                              <div className="px-3 py-2 text-sm text-white">
                                                Not on this campaign — add "{item.manualCreatorName}" via "Add Creators" first, then re-add the content.
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="px-4 py-2 border border-white/20 rounded-md text-white hover:bg-white/5"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleBulkAdd}
                          disabled={addingContent || loadingPreviews || urlItems.filter(i => i.selected).length === 0}
                          className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 disabled:bg-white/20"
                          title={urlItems.filter(i => i.selected).length === 0 ? 'Select at least one item' : ''}
                        >
                          {addingContent ? 'Adding...' : `Add ${urlItems.filter(i => i.selected).length} Item${urlItems.filter(i => i.selected).length !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Results View */
                <div className="py-6">
                  {/* Summary Header */}
                  <div className="text-center mb-6">
                    <div className="text-4xl mb-2">
                      {results.success.length > 0 ? '✅' : results.duplicates?.length > 0 ? '⚠️' : '❌'}
                    </div>
                    <p className="text-lg font-semibold text-white">
                      Upload Complete
                    </p>
                  </div>

                  {/* Stats Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-300">{results.success.length}</p>
                      <p className="text-sm text-green-300">Added</p>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-yellow-300">{results.duplicates?.length || 0}</p>
                      <p className="text-sm text-yellow-300">Duplicates</p>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-red-300">{results.failed.length}</p>
                      <p className="text-sm text-red-300">Failed</p>
                    </div>
                  </div>

                  {/* Success Details */}
                  {results.success.length > 0 && (
                    <div className="mb-4 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <p className="font-semibold text-green-300 mb-2 flex items-center gap-2">
                        ✅ Successfully Added ({results.success.length})
                      </p>
                      <ul className="text-sm text-green-300 space-y-1 max-h-32 overflow-y-auto">
                        {results.success.map((item, idx) => (
                          <li key={idx} className="truncate">• {item.title}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Duplicates Details */}
                  {results.duplicates?.length > 0 && (
                    <div className="mb-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <p className="font-semibold text-yellow-300 mb-2 flex items-center gap-2">
                        ⚠️ Already Exists ({results.duplicates.length})
                      </p>
                      <ul className="text-sm text-yellow-300 space-y-1 max-h-32 overflow-y-auto">
                        {results.duplicates.map((item, idx) => (
                          <li key={idx} className="truncate">• {item.title}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Failed Details */}
                  {results.failed.length > 0 && (
                    <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <p className="font-semibold text-red-300 mb-2 flex items-center gap-2">
                        ❌ Failed ({results.failed.length})
                      </p>
                      <ul className="text-sm text-red-300 space-y-1 max-h-32 overflow-y-auto">
                        {results.failed.map((item, idx) => (
                          <li key={idx} className="truncate">• {item.title} - {item.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {results.rejected?.length > 0 && (
                    <div className="mb-4 bg-sc-pink/10 border border-sc-pink/40 rounded-lg p-4">
                      <p className="font-semibold text-white mb-2 flex items-center gap-2">
                        🚫 Rejected — creator not on this campaign ({results.rejected.length})
                      </p>
                      <p className="text-xs text-white mb-2">
                        No creator was created. Add the creator via <strong>Add Creators</strong> first, then re-add. These are logged in the campaign's <strong>Rejected Content</strong> box.
                      </p>
                      <ul className="text-sm text-white space-y-1 max-h-32 overflow-y-auto">
                        {results.rejected.map((item, idx) => (
                          <li key={idx} className="truncate">• {item.title} — {item.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Auto-close message */}
                  {results.success.length > 0 && (
                    <p className="text-sm text-white text-center mt-4">
                      Closing automatically...
                    </p>
                  )}

                  {/* Close button if no success */}
                  {results.success.length === 0 && (
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={() => { resetForm(); onClose(); }}
                        className="px-4 py-2 bg-white/10 text-white rounded-md hover:bg-white/20"
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* SINGLE MODE */}
          {mode === 'single' && (
            <form onSubmit={handleSingleSubmit}>
              {/* URL Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-white mb-2">Content URL *</label>
                <div className="relative">
                  <input
                    type="url"
                    name="contentUrl"
                    value={formData.contentUrl}
                    onChange={handleUrlChange}
                    required
                    className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
                    placeholder="Paste YouTube, Twitch, TikTok, or Instagram URL..."
                  />
                  {scraping && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin h-5 w-5 border-2 border-sc-pink border-t-transparent rounded-full"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Scraped Creator Info */}
              {scrapedCreator && (
                <div className={`mb-4 ${scrapedCreator.isNew !== false ? 'bg-green-500/10 border-green-500/30' : 'bg-sc-pink/10 border-sc-pink/30'} border rounded-lg p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">
                    <PlatformIcon platform={scrapedCreator.platform} size="lg" />
                  </div>
                  <div>
                    <p className={`font-medium ${scrapedCreator.isNew !== false ? 'text-green-300' : 'text-sc-purple'}`}>
                      {scrapedCreator.name}
                    </p>
                    <p className={`text-sm ${scrapedCreator.isNew !== false ? 'text-green-300' : 'text-sc-pink'}`}>
                      @{scrapedCreator.handle} • {scrapedCreator.followerCount?.toLocaleString()} {getFollowerLabel(scrapedCreator.platform).toLowerCase()}
                    </p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  scrapedCreator.isNew !== false 
                    ? 'bg-green-200 text-green-300' 
                    : 'bg-sc-pink/20 text-sc-pink'
                }`}>
                  {scrapedCreator.isNew !== false ? '✨ New Creator Added' : '✓ Existing Creator'}
                </span>
              </div>
            </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Platform */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Platform</label>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
              >
                <option value="youtube">YouTube</option>
                <option value="twitch">Twitch</option>
                <option value="kick">Kick</option>
                <option value="tiktok">TikTok</option>
                <option value="instagram">Instagram</option>
                <option value="twitter">Twitter/X</option>
              </select>
            </div>

            {/* Content Type */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Content Type</label>
              <select
                name="contentType"
                value={formData.contentType}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
              >
                <option value="video">Video</option>
                <option value="stream">Live Stream</option>
                <option value="vod">VOD</option>
                <option value="short">Short</option>
                <option value="reel">Reel</option>
                <option value="post">Post</option>
              </select>
            </div>

            {/* Creator (if not auto-detected) */}
            {!scrapedCreator && (
              <div>
                <label className="block text-sm font-medium text-white mb-2">Creator *</label>
                <select
                  name="creatorId"
                  value={formData.creatorId}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
                >
                  <option value="">Select creator...</option>
                  {creators.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} (@{c.handle})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Existing creator dropdown (if scraped creator matched) */}
            {scrapedCreator && formData.creatorId && (
              <div>
                <label className="block text-sm font-medium text-white mb-2">Creator (Matched)</label>
                <select
                  name="creatorId"
                  value={formData.creatorId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-green-500/10 text-white placeholder-white/50"
                >
                  {creators.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} (@{c.handle})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Game */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Game</label>
              <select
                name="gameId"
                value={formData.gameId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
              >
                <option value="">Select game...</option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>{g.title}</option>
                ))}
              </select>
            </div>

            {/* Post Date */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Post Date</label>
              <input
                type="datetime-local"
                name="postDate"
                value={formData.postDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
              />
            </div>

            {/* Stats - Auto-filled */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Views</label>
              <input
                type="number"
                name="views"
                value={formData.views}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">Likes</label>
              <input
                type="number"
                name="likes"
                value={formData.likes}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">Comments</label>
              <input
                type="number"
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">Shares</label>
              <input
                type="number"
                name="shares"
                value={formData.shares}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
              />
            </div>

            {/* Twitch-specific */}
            {showTwitchFields && (
              <>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Peak CCV</label>
                  <input
                    type="number"
                    name="peakCcv"
                    value={formData.peakCcv}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">Avg CCV</label>
                  <input
                    type="number"
                    name="avgCcv"
                    value={formData.avgCcv}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">Stream Duration (mins)</label>
                  <input
                    type="number"
                    name="streamDuration"
                    value={formData.streamDuration}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">VOD Views</label>
                  <input
                    type="number"
                    name="vodViews"
                    value={formData.vodViews}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink bg-white/5 text-white placeholder-white/50"
                  />
                </div>
              </>
            )}

            {/* Game Segments - Show for streams/VODs */}
            {(formData.contentType === 'stream' || formData.contentType === 'vod') && (
              <div className="md:col-span-2 mt-4">
                <SegmentManager
                  segments={segments}
                  games={games}
                  onSegmentsChange={setSegments}
                />
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-white/20 rounded-md text-white hover:bg-white/5">
              Cancel
            </button>
            <button type="submit" disabled={loading || scraping} className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 disabled:bg-white/20">
              {loading ? 'Adding...' : 'Add Content'}
            </button>
          </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddContentModal;
